<template>
  <!--
    Rail-native Admin group (LHS-rework Screen B) — the expand-in-place
    replacement for the deleted AdminSubnav popout. When the current route is
    under an admin prefix, the Admin entry's sub-sections render INLINE in the
    rail (not a separate 220px column). Fed by the same settings-tiles endpoint
    that drives the /admin tile hub, so the two never drift.
  -->
  <div v-if="shouldRender" class="admin-rail-group" aria-label="Admin navigation">
    <div v-if="loading" class="admin-rail-group__loading">
      <CygnusIcon name="spinner" :spin="true" />
    </div>

    <template v-else>
      <div
        v-for="group in groups"
        :key="group.key"
        class="admin-rail-group__section"
        :class="{ 'is-gated': group.locked }"
      >
        <div class="admin-rail-group__subhead" :class="{ 'is-enterprise': group.gate }">
          <span class="admin-rail-group__subhead-label">{{ group.title }}</span>
          <span v-if="group.gate" class="admin-rail-group__ent-chip">
            <CygnusIcon name="award" /> Enterprise
          </span>
        </div>

        <template v-for="tile in group.tiles" :key="tile.title">
          <!-- Locked (Enterprise upsell) — honest, non-navigating placeholder. -->
          <a
            v-if="tile.locked"
            class="admin-rail-group__link is-locked"
            href="#"
            @click.prevent="onCompare"
          >
            <CygnusIcon :name="tile.icon || 'question'" />
            <span class="admin-rail-group__label">{{ tile.title }}</span>
            <span class="admin-rail-group__lock"><CygnusIcon name="lock" /> Ent</span>
          </a>

          <router-link
            v-else
            :to="tile.route"
            class="admin-rail-group__link"
            active-class="is-active"
          >
            <CygnusIcon :name="tile.icon || 'question'" />
            <span class="admin-rail-group__label">{{ tile.title }}</span>
          </router-link>
        </template>

        <!-- Compare-plans quiet link under a locked cluster (not "Upgrade"). -->
        <router-link
          v-if="group.locked"
          to="/admin/usage-plans"
          class="admin-rail-group__compare"
        >
          Compare plans <CygnusIcon name="arrow-right" />
        </router-link>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * AdminRailGroup (icarus-theme-base) — renders the Admin area's sub-sections
 * inline in the rail when the current route is under an admin prefix. Replaces
 * the fixed-position AdminSubnav column (deleted in the LHS rework).
 *
 * Convention: reads `GET /api/v1/shell/admin/settings-tiles` returning
 * `{ groups: [{ key, title, tiles: [{ route, title, icon, locked }], locked, gate }] }`.
 * A feature-gated group (e.g. Data views on `data_views`) arrives with
 * `locked: true` for operators on a non-Enterprise plan — its tiles render as
 * honest "Included in Enterprise" placeholders with a Compare-plans link. Groups
 * the caller can't reach at all are simply absent from the payload.
 *
 * Missing / erroring endpoint = silent empty render (no console noise).
 */
import useApi from '@apexl/icarus-core/src/services/api';

export default {
  name: 'AdminRailGroup',
  props: {
    endpoint: { type: String, default: '/api/v1/shell/admin/settings-tiles' },
    /** Route prefix(es) that trigger visibility. Single string or array. */
    visibleUnder: { type: [String, Array], default: '/admin' },
  },
  data() {
    return {
      groups: [],
      loading: false,
      hasFetched: false,
    };
  },
  computed: {
    prefixes() {
      return Array.isArray(this.visibleUnder) ? this.visibleUnder : [this.visibleUnder];
    },
    visible() {
      const path = this.$route?.path || '';
      return this.prefixes.some(p => p && path.startsWith(p));
    },
    hasContent() {
      return this.groups.some(g => Array.isArray(g.tiles) && g.tiles.length > 0);
    },
    shouldRender() {
      return this.visible && (this.loading || this.hasContent);
    },
  },
  watch: {
    visible(now) {
      if (now && !this.hasFetched) this.load();
    },
  },
  mounted() {
    if (this.visible) this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await useApi().get(this.endpoint);
        this.groups = res?.groups || [];
        this.hasFetched = true;
      } catch {
        this.groups = []; // silent — endpoint may not be registered
      } finally {
        this.loading = false;
      }
    },
    onCompare() {
      if (this.$route?.path !== '/admin/usage-plans') {
        this.$router.push('/admin/usage-plans');
      }
    },
  },
};
</script>

<style lang="scss">
// Inline admin sub-nav — token-driven so the spine + sub-item states read
// correctly on BOTH the light (default) rail and the ink (dark) rail.
//
// This block used to be hardcoded for the dark rail only (raw
// `rgba(255,255,255,*)` / `#fff` throughout) — invisible-to-illegible on the
// light rail (white hairline on white, white hover/active text on a light
// fill). Re-pointed at the same --theme-rail-* family the rest of the rail
// uses. The hairline spine now matches the approved mockup's `.navsub`
// treatment (Plans/ui-mockups/fable-review/musubi-lhs-admin-mockup.html —
// a subtle `--line`-toned spine, not a hard/bright rule); dark keeps its
// existing look since --theme-rail-hairline resolves to the same
// translucent-white value it was hardcoded to before.
.admin-rail-group {
  margin: 2px 0 8px 1.35rem;
  padding-left: 0.7rem;
  border-left: 1px solid var(--theme-rail-hairline);

  &__loading {
    padding: 0.6rem 0.75rem;
    color: var(--theme-rail-text-muted);
    font-size: 0.82rem;
  }

  &__section { padding: 2px 0; }

  &__subhead {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.58rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--theme-rail-text-muted);
    padding: 0.7rem 0.75rem 0.3rem;

    &.is-enterprise { color: var(--theme-violet); }
  }

  &__subhead-label { white-space: nowrap; }

  &__ent-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.55rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    color: var(--theme-violet);
    background: var(--theme-violet-soft);
    border: 1px solid var(--theme-violet-line);
    border-radius: 999px;
    padding: 1px 6px;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.42rem 0.75rem;
    color: var(--theme-text-sidebar);
    font-size: 0.8rem;
    text-decoration: none;
    border-radius: 6px;
    border-left: 2px solid transparent;
    transition: background 0.12s, color 0.12s;

    i { width: 1rem; text-align: center; font-size: 0.82rem; opacity: 0.75; }

    &:hover {
      background: var(--theme-rail-hover-bg);
      color: var(--theme-rail-text-strong);
    }

    &.is-active {
      background: var(--theme-rail-active-bg);
      color: var(--theme-text-sidebar-active);
      border-left-color: var(--theme-rail-active-marker);
      i { opacity: 1; color: var(--theme-rail-active-marker); }
    }

    // Enterprise-locked placeholder — violet identity, muted, non-navigating.
    &.is-locked {
      color: var(--theme-rail-text-muted);
      cursor: pointer;
      i { opacity: 0.5; }
      &:hover { background: var(--theme-violet-soft); color: var(--theme-rail-text-subtle); }
    }
  }

  &__label { flex: 1 1 auto; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  &__lock {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.55rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--theme-violet);
    background: var(--theme-violet-soft);
    border: 1px solid var(--theme-violet-line);
    border-radius: 999px;
    padding: 1px 6px;
    i { width: auto; opacity: 1; font-size: 0.6rem; }
  }

  &__compare {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 6px 0.75rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--theme-violet);
    text-decoration: none;
    &:hover { text-decoration: underline; }
    i { font-size: 0.65rem; }
  }
}
</style>
